import { LogOut, Sun, Moon } from 'lucide-react';
import { useT } from '@/lib/i18n';
import { useAuth } from '@/hooks/useAuth';
import { useThemeStore } from '@/stores/themeStore';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';

/**
 * UserMenu — avatar + dropdown :
 *   - En-tête : nom + rôle principal
 *   - Toggle dark/light
 *   - Séparateur
 *   - Déconnexion
 */
export function UserMenu() {
  const t = useT();
  const { user, logout } = useAuth();
  const theme = useThemeStore((s) => s.theme);
  const toggleTheme = useThemeStore((s) => s.toggleTheme);

  const initials = user?.fullName
    ? user.fullName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : '??';

  const primaryRole = user?.roles?.[0] ?? '';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 rounded-full p-0 font-semibold text-xs bg-brand-samsung-500/20 hover:bg-brand-samsung-500/30 text-brand-samsung-500"
          aria-label={t('commun.menuUtilisateur')}
        >
          {initials}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-56">
        {/* En-tête */}
        <DropdownMenuLabel className="font-normal">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-samsung-500/20 text-brand-samsung-500 text-xs font-semibold">
              {initials}
            </div>
            <div className="flex flex-col space-y-0.5 overflow-hidden">
              <p className="truncate text-sm font-medium leading-none">
                {user?.fullName ?? '—'}
              </p>
              <p className="truncate text-xs text-muted-foreground">
                {user?.email ?? '—'}
              </p>
              {primaryRole && (
                <p className="truncate text-xs text-muted-foreground/70">
                  {primaryRole.replace(/_/g, ' ')}
                </p>
              )}
            </div>
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        {/* `#1060` — Bascule de thème (persistée, sans rechargement). L'icône et
            le libellé annoncent la CIBLE : en sombre → « Mode clair » + soleil. */}
        <DropdownMenuItem
          onSelect={(e) => { e.preventDefault(); toggleTheme(); }}
          className="cursor-pointer gap-2"
          data-testid="theme-toggle"
        >
          {theme === 'dark'
            ? <><Sun className="h-4 w-4" />{t('theme.modeClair')}</>
            : <><Moon className="h-4 w-4" />{t('theme.modeSombre')}</>}
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Déconnexion */}
        <DropdownMenuItem
          onSelect={() => void logout()}
          className="cursor-pointer gap-2 text-accent-danger focus:text-accent-danger"
        >
          <LogOut className="h-4 w-4" />
          {t('commun.seDeconnecter')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
